import AsyncStorage from '@react-native-async-storage/async-storage'
import { setDefaultOptions } from 'date-fns'
import React, { PropsWithChildren, useCallback, useContext, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Platform } from 'react-native'
import useSWRImmutable from 'swr/immutable'

import { FALLBACK_LANGUAGE } from '../constants/config'
import { useResetSwrCache } from '../hooks/use-swr-reset-cache'
import fetcher from '../lib/fetcher'
import { supportedLocales } from '../services/i18n/supportedLocales'
import { SupportedLanguageKeys, SupportedLocale } from '../services/i18n/types'

import { useConfig } from './config'

export interface Locales {
  [k: string]: string | Locales
}

type ContextType = {
  isLoading: boolean
  error?: string
  currentLanguage: string
  supportedLanguages?: SupportedLocale[]
  changeLanguage: (lang: string) => void
}

const SUPPORTED_LOCALES: { code: string; name: string }[] = Object.entries(supportedLocales).map(
  ([code, { name }]) => ({
    code,
    name,
  })
)

const getDateFnsLocale = (lang: string) => {
  switch (lang) {
    case SupportedLanguageKeys.EN:
    case SupportedLanguageKeys.EN_US:
      return require('date-fns/locale/en-US')
    case SupportedLanguageKeys.ES:
    case SupportedLanguageKeys.ES_ES:
      return require('date-fns/locale/es')
    default:
      return new Error(`Unsupported locale: ${lang}`)
  }
}

export const LocalesContext = React.createContext<ContextType | undefined>(undefined)

export const LocalesProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { i18n } = useTranslation()
  const { config } = useConfig()
  const resetSwrCache = useResetSwrCache()

  const { data: supportedLanguages } = useSWRImmutable<SupportedLocale[]>('/locales', {
    fetcher: fetcher.api,
  })

  const { data, error } = useSWRImmutable<Locales>(`/patient/locales/${i18n.language}`, {
    fetcher: fetcher.api,
  })

  const changeLanguage = useCallback(
    async (lang: string) => {
      i18n.changeLanguage(lang)
      resetSwrCache()
    },
    [i18n, resetSwrCache]
  )

  useEffect(
    function addTranslations() {
      if (data) {
        i18n.addResourceBundle(i18n.language, 'translations', data, true, true)
      }
    },
    [data, i18n]
  )

  useEffect(
    function ensureLanguageSupport() {
      // Check for app configured language value
      if (config.patientAppLanguage) {
        i18n.changeLanguage(config.patientAppLanguage).catch(() => {
          // If the configured language is not supported, we fallback to the default language
          i18n.changeLanguage(FALLBACK_LANGUAGE)
        })

        return
      }

      if (!supportedLanguages) return

      // First check if the current language is supported
      const exactMatch = supportedLanguages.some(l => l.code === i18n.language)
      if (exactMatch) return

      // If the browser's locale is not supported, we try to find one in Strapi that matches the same language
      // (e.g. 'es-ES' -> 'es-ES')
      const fallbackSameLanguage = supportedLanguages.find(l => l.code.split('-')[0] === i18n.language?.split('-')[0])

      if (fallbackSameLanguage) {
        i18n.changeLanguage(fallbackSameLanguage.code)
        return
      }

      // Strapi doesn't support the browser's locale, so we fallback to the default language
      const hasLocalLocale = SUPPORTED_LOCALES.some(l => l.code.split('-')[0] === i18n.language?.split('-')[0])
      if (!hasLocalLocale) {
        i18n.changeLanguage(FALLBACK_LANGUAGE)
      }
    },
    [supportedLanguages, i18n]
  )

  useEffect(
    function subscribeToStorage() {
      if (Platform.OS === 'web') {
        const handleStorage = async () => {
          const lang = await AsyncStorage.getItem('lang')
          if (lang && lang !== i18n.language) {
            changeLanguage(lang)
          }
        }

        window.addEventListener('storage', handleStorage)

        return () => window.removeEventListener('storage', handleStorage)
      }
    },
    [changeLanguage, i18n.language]
  )

  useEffect(() => {
    setDefaultOptions({ locale: getDateFnsLocale(i18n.language).default })
  }, [i18n.language])

  const value = useMemo(() => {
    return { isLoading: !data && !error, error, currentLanguage: i18n.language, supportedLanguages, changeLanguage }
  }, [data, error, i18n, supportedLanguages, changeLanguage])

  return <LocalesContext.Provider value={value}>{children}</LocalesContext.Provider>
}

export const useLocales = () => {
  const context = useContext(LocalesContext)

  if (!context) {
    throw new Error('useLocales() must be wrapped inside LocalesProvider.')
  }

  return context
}
